import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  Users,
  Search,
  RefreshCw,
  Coins,
  CheckCircle2,
  Clock,
  ArrowRight,
  TrendingUp,
  RotateCcw,
  ShoppingBag,
  Zap,
  GraduationCap
} from "lucide-react";
import { toast } from "sonner";

export default function AdminReferralsTab() {
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // "all" | "completed" | "pending"
  const [resetting, setResetting] = useState(false);

  const fetchReferrals = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/referrals/admin/all`, { withCredentials: true });
      setReferrals(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Failed to fetch admin referrals:", err);
      toast.error("Failed to load referrals list");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferrals();
  }, [fetchReferrals]);

  const handleResetAllCoins = async () => {
    if (!window.confirm("Are you sure you want to reset ALL users' coin balances to 0? This cannot be undone.")) {
      return;
    }
    setResetting(true);
    try {
      await axios.post(`${API}/referrals/admin/reset-all-coins`, {}, { withCredentials: true });
      toast.success("All users' coins have been reset to 0.");
      fetchReferrals();
    } catch (err) {
      toast.error("Failed to reset coins");
    } finally {
      setResetting(false);
    }
  };

  const filteredReferrals = referrals.filter((ref) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      (ref.referrer_name || "").toLowerCase().includes(q) ||
      (ref.referrer_email || "").toLowerCase().includes(q) ||
      (ref.referrer_code || "").toLowerCase().includes(q) ||
      (ref.referred_name || "").toLowerCase().includes(q) ||
      (ref.referred_email || "").toLowerCase().includes(q) ||
      (ref.product_purchased || "").toLowerCase().includes(q);

    const isCompleted = ref.status === "completed" || ref.status === "successful";
    if (statusFilter === "completed" && !isCompleted) return false;
    if (statusFilter === "pending" && isCompleted) return false;

    return matchesSearch;
  });

  const totalCompleted = referrals.filter((r) => r.status === "completed" || r.status === "successful").length;
  const totalPending = referrals.length - totalCompleted;
  const totalCoinsPaid = referrals
    .filter((r) => r.status === "completed" || r.status === "successful")
    .reduce((sum, r) => sum + Number(r.coins_awarded || r.reward_amount || 0), 0);

  return (
    <div className="space-y-5" data-testid="admin-referrals-tab">
      
      {/* Top Header Card */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-5 rounded-2xl bg-white dark:bg-[#131B2C] border border-gray-200 dark:border-white/[0.08] shadow-sm">
        <div>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-[#F59E0B]/15 flex items-center justify-center text-[#F59E0B]">
              <TrendingUp className="w-5 h-5" />
            </div>
            <h2 className="text-lg sm:text-xl font-bold font-outfit text-gray-900 dark:text-white">
              Referral &amp; Affiliate Commission Network
            </h2>
          </div>
          <p className="text-xs text-gray-500 dark:text-zinc-400 font-plex mt-1">
            Real-time tracking of who referred whom, which course or Drishti AI plan was purchased, and 10% coin rewards (1 Coin = ₹1).
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchReferrals}
            disabled={loading}
            className="text-xs rounded-xl"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>

          <Button
            variant="destructive"
            size="sm"
            onClick={handleResetAllCoins}
            disabled={resetting}
            className="text-xs rounded-xl font-bold"
            title="Emergency tool to re-zero all user balances"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
            {resetting ? "Resetting..." : "Reset All Coins to 0"}
          </Button>
        </div>
      </div>

      {/* 4 Summary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06] shadow-sm">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center text-blue-500">
                <Users className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-extrabold font-jetbrains text-gray-900 dark:text-white">
                  {referrals.length}
                </p>
                <p className="text-xs text-gray-500 dark:text-zinc-400 font-outfit">Total Referrals</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06] shadow-sm">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center text-emerald-500">
                <ShoppingBag className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-extrabold font-jetbrains text-emerald-600 dark:text-[#00FF88]">
                  {totalCompleted}
                </p>
                <p className="text-xs text-gray-500 dark:text-zinc-400 font-outfit">Purchases Completed</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06] shadow-sm">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center text-amber-500">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-extrabold font-jetbrains text-amber-600 dark:text-amber-400">
                  {totalPending}
                </p>
                <p className="text-xs text-gray-500 dark:text-zinc-400 font-outfit">Pending Purchases</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06] shadow-sm">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#FFCC00]/15 flex items-center justify-center text-[#FFCC00]">
                <Coins className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-extrabold font-jetbrains text-[#FFCC00]">
                  ₹{totalCoinsPaid.toLocaleString("en-IN")}
                </p>
                <p className="text-xs text-gray-500 dark:text-zinc-400 font-outfit">
                  Total Rewards ({totalCoinsPaid} Coins)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 p-3.5 rounded-xl bg-white dark:bg-[#131B2C] border border-gray-200 dark:border-white/[0.06]">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by referrer, student, code, or product..."
            className="pl-9 h-9 text-xs rounded-lg"
          />
        </div>

        <div className="flex items-center gap-1.5 self-end sm:self-center">
          <Button
            size="sm"
            variant={statusFilter === "all" ? "default" : "outline"}
            onClick={() => setStatusFilter("all")}
            className="text-xs h-8 rounded-lg"
          >
            All ({referrals.length})
          </Button>
          <Button
            size="sm"
            variant={statusFilter === "completed" ? "default" : "outline"}
            onClick={() => setStatusFilter("completed")}
            className="text-xs h-8 rounded-lg"
          >
            Purchased ({totalCompleted})
          </Button>
          <Button
            size="sm"
            variant={statusFilter === "pending" ? "default" : "outline"}
            onClick={() => setStatusFilter("pending")}
            className="text-xs h-8 rounded-lg"
          >
            Pending ({totalPending})
          </Button>
        </div>
      </div>

      {/* Referrals Detailed Table */}
      <Card className="bg-white dark:bg-[#131B2C] border-gray-200 dark:border-white/[0.06] shadow-sm overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 dark:bg-white/[0.02] border-b border-gray-200 dark:border-white/[0.06] text-gray-500 dark:text-zinc-400 font-outfit uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="py-3 px-4">Referrer (Invited By)</th>
                  <th className="py-3 px-4">Referred Student</th>
                  <th className="py-3 px-4">Product Purchased</th>
                  <th className="py-3 px-4 text-right">Commission Awarded</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-right">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-white/[0.04]">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12">
                      <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : filteredReferrals.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-gray-400 font-plex">
                      No referral records found matching your filters.
                    </td>
                  </tr>
                ) : (
                  filteredReferrals.map((ref, idx) => {
                    const isCompleted = ref.status === "completed" || ref.status === "successful";
                    const isDrishti = (ref.product_purchased || "").toLowerCase().includes("drishti");
                    const coins = Number(ref.coins_awarded || ref.reward_amount || (isCompleted ? 100 : 0));

                    return (
                      <tr key={ref.referral_id || idx} className="hover:bg-gray-50/70 dark:hover:bg-white/[0.02] transition-colors">
                        
                        {/* Referrer */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-2.5">
                            <Avatar className="w-8 h-8 border border-gray-200 dark:border-white/10 shrink-0">
                              <AvatarFallback className="bg-amber-500/10 text-amber-600 font-bold text-xs">
                                {(ref.referrer_name || "R").charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="font-bold text-gray-900 dark:text-white font-outfit truncate">
                                {ref.referrer_name || "Unknown Referrer"}
                              </p>
                              <p className="text-[11px] text-gray-400 dark:text-zinc-400 font-mono truncate">
                                {ref.referrer_email}
                              </p>
                              <div className="flex items-center gap-1.5 mt-0.5">
                                <span className="font-mono text-[10px] font-bold text-emerald-600 dark:text-[#00FF88] bg-emerald-500/10 px-1.5 py-0.2 rounded">
                                  {ref.referrer_code || "CODE"}
                                </span>
                                <span className="text-[10px] text-amber-500 font-jetbrains">
                                  Wallet: {ref.referrer_coins || 0} Coins
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Referred Student */}
                        <td className="py-3.5 px-4">
                          <div className="flex items-center gap-2.5">
                            <Avatar className="w-8 h-8 border border-gray-200 dark:border-white/10 shrink-0">
                              <AvatarFallback className="bg-blue-500/10 text-blue-600 font-bold text-xs">
                                {(ref.referred_name || "S").charAt(0)}
                              </AvatarFallback>
                            </Avatar>
                            <div className="min-w-0">
                              <p className="font-bold text-gray-900 dark:text-white font-outfit truncate">
                                {ref.referred_name || "Student Member"}
                              </p>
                              <p className="text-[11px] text-gray-400 dark:text-zinc-400 font-mono truncate">
                                {ref.referred_email}
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Product Purchased */}
                        <td className="py-3.5 px-4">
                          {isCompleted ? (
                            <div className="flex items-center gap-1.5">
                              {isDrishti ? (
                                <Zap className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                              ) : (
                                <GraduationCap className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                              )}
                              <div>
                                <p className="font-bold text-gray-900 dark:text-white font-outfit truncate">
                                  {ref.product_purchased || "Drishti AI Pro"}
                                </p>
                                {Number(ref.purchase_amount || 0) > 0 && (
                                  <p className="text-[11px] font-mono text-emerald-600 dark:text-emerald-400">
                                    Paid: ₹{Number(ref.purchase_amount).toLocaleString("en-IN")}
                                  </p>
                                )}
                              </div>
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] font-plex text-amber-600 dark:text-amber-400 bg-amber-500/10 px-2 py-0.5 rounded-full border border-amber-500/20">
                              <Clock className="w-3 h-3" />
                              Awaiting Purchase
                            </span>
                          )}
                        </td>

                        {/* Commission */}
                        <td className="py-3.5 px-4 text-right">
                          {isCompleted ? (
                            <div>
                              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-jetbrains font-bold bg-emerald-500/15 text-emerald-600 dark:text-[#00FF88] border border-emerald-500/30">
                                +{coins} Coins
                              </span>
                              <p className="text-[10px] text-gray-400 font-mono mt-0.5">
                                = ₹{coins}
                              </p>
                            </div>
                          ) : (
                            <span className="text-gray-400 font-mono text-xs">0 Coins</span>
                          )}
                        </td>

                        {/* Status */}
                        <td className="py-3.5 px-4 text-center">
                          {isCompleted ? (
                            <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-[#00FF88] border border-emerald-500/30 text-[10px] font-mono">
                              <CheckCircle2 className="w-3 h-3 mr-1 inline" />
                              Purchased
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 text-[10px] font-mono">
                              Pending Purchase
                            </Badge>
                          )}
                        </td>

                        {/* Date */}
                        <td className="py-3.5 px-4 text-right text-gray-500 dark:text-zinc-400 font-mono text-[11px]">
                          {ref.purchased_at || ref.created_at
                            ? new Date(ref.purchased_at || ref.created_at).toLocaleDateString("en-IN", {
                                day: "numeric",
                                month: "short",
                                year: "numeric"
                              })
                            : "Recent"}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

    </div>
  );
}
